import React, { useLayoutEffect } from 'react';
import { useNativePush } from './utils/useNativePush';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SocketProvider } from './contexts/SocketContext';
import { ToastProvider } from './components/Toast';
import { ConfirmProvider } from './components/Modal';

import LoginPage from './pages/LoginPage';
import WorkspacePage from './pages/WorkspacePage';
import RequestPage from './pages/RequestPage';
import HistoryPage from './pages/HistoryPage';
import QRCodePage from './pages/QRCodePage';
import MePage from './pages/MePage';
import AdminDashboard from './pages/admin/AdminDashboard';
import ImportPage from './pages/admin/ImportPage';
import UserManagementPage from './pages/admin/UserManagementPage';
import AdminMePage from './pages/admin/AdminMePage';
import SettlementPage from './pages/admin/SettlementPage';
import ProxyRequestPage from './pages/admin/ProxyRequestPage';
import DispatchViewPage from './pages/admin/DispatchViewPage';
import DispatchDetailsPage from './pages/admin/DispatchDetailsPage';
import VerificationCodePage from './pages/admin/VerificationCodePage';
import WorkHoursPage from './pages/admin/WorkHoursPage';
import TutorialSettingsPage from './pages/admin/TutorialSettingsPage';
import QRCodeManagePage from './pages/admin/QRCodeManagePage';
import SettlementOverviewPage from './pages/admin/SettlementOverviewPage';
import AgentSettlementOverviewPage from './pages/admin/AgentSettlementOverviewPage';
import PhoneQueryPage from './pages/admin/PhoneQueryPage';
import ImageManagePage from './pages/admin/ImageManagePage';
import PushMessagePage from './pages/admin/PushMessagePage';
import SubmissionRatePage from './pages/admin/SubmissionRatePage';
import DailySummaryPage from './pages/admin/DailySummaryPage';
import AdminOcrPage from './pages/admin/AdminOcrPage';
import RateControlPage from './pages/admin/RateControlPage';
import VideoReviewPage from './pages/admin/VideoReviewPage';
import VideoReviewUserPage from './pages/VideoReviewUserPage';
import WeChatBlocker, { isWeChatBrowser } from './components/WeChatBlocker';
import MaintenanceGate from './components/MaintenanceGate';
import NotifyPermissionPrompt from './components/NotifyPermissionPrompt';
import PuzzleRecordsPage from './pages/PuzzleRecordsPage';
import PuzzlePackagePage from './pages/admin/PuzzlePackagePage';
import PuzzleProxyPage from './pages/admin/PuzzleProxyPage';
import AdminPuzzleRecordsPage from './pages/admin/AdminPuzzleRecordsPage';

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>加载中...</div>;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminRoute({ children }) {
  const { user, isAdminOrAgent, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>加载中...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdminOrAgent) return <Navigate to="/" replace />;
  return children;
}

// 仅管理员可进（数据包管理：导入/删除号码数据）
// 旧地址 /admin/puzzle-images 已并入“证券识别记录”页的「图片管理」标签；保留原有的 ?userId 等参数
function PuzzleImagesRedirect() {
  const { search } = useLocation();
  const p = new URLSearchParams(search);
  p.set('tab', 'images');
  return <Navigate to={`/admin/puzzle-records?${p.toString()}`} replace />;
}

function AdminOnlyRoute({ children }) {
  const { user, isAdmin, loading } = useAuth();
  if (loading) return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh' }}>加载中...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (!isAdmin) return <Navigate to="/admin" replace />;
  return children;
}

// 页面切换（pathname 变化）时：
//   1. 滚动回到顶部。这是单页应用，切页并不会刷新文档，浏览器会把上一页的滚动位置原样带到新页面，
//      于是新页面一进来就是“上滑了一小段”的状态；同时也不再依赖浏览器自己的滚动恢复（前进/后退时它会乱恢复）。
//   2. 通知 viewportFix 把顶栏 / 底栏的位置补偿值清零并重新校准，避免上一页残留的补偿值带到新页面（表现为切页后顶栏错位、内容从时间栏后面透出来）。
// 只看 pathname：同一页面里改 ?tab= 之类的查询参数不应该把页面滚回顶部。
if (typeof window !== 'undefined' && 'scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual';
}
function RouteScrollReset() {
  const { pathname } = useLocation();
  useLayoutEffect(() => {
    window.scrollTo(0, 0);
    if (document.documentElement) document.documentElement.scrollTop = 0;
    if (document.body) document.body.scrollTop = 0;
    try { window.dispatchEvent(new Event('app:route-change')); } catch (e) { /* ignore */ }
  }, [pathname]);
  return null;
}

function AppRoutes() {
  useNativePush(); // 安卓 APK 前台服务长连接推送（浏览器自动跳过）
  return (
    <MaintenanceGate>
    <RouteScrollReset />
    <NotifyPermissionPrompt />   {/* 没开通知的用户进入后弹窗引导授权 */}
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/" element={<ProtectedRoute><WorkspacePage /></ProtectedRoute>} />
      <Route path="/request" element={<ProtectedRoute><RequestPage /></ProtectedRoute>} />
      <Route path="/history" element={<ProtectedRoute><HistoryPage /></ProtectedRoute>} />
      <Route path="/qrcode" element={<ProtectedRoute><QRCodePage /></ProtectedRoute>} />
      <Route path="/me" element={<ProtectedRoute><MePage /></ProtectedRoute>} />
      <Route path="/puzzle/records" element={<ProtectedRoute><PuzzleRecordsPage /></ProtectedRoute>} />
      <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
      <Route path="/admin/import" element={<AdminRoute><ImportPage /></AdminRoute>} />
      <Route path="/admin/users" element={<AdminRoute><UserManagementPage /></AdminRoute>} />
      <Route path="/admin/me" element={<AdminRoute><AdminMePage /></AdminRoute>} />
      <Route path="/admin/settlement" element={<AdminRoute><SettlementPage /></AdminRoute>} />
      <Route path="/admin/proxy" element={<AdminRoute><ProxyRequestPage /></AdminRoute>} />
      <Route path="/admin/dispatch-details" element={<AdminRoute><DispatchDetailsPage /></AdminRoute>} />
      <Route path="/admin/dispatch-view/:userId" element={<AdminRoute><DispatchViewPage /></AdminRoute>} />
      <Route path="/admin/verification" element={<AdminRoute><VerificationCodePage /></AdminRoute>} />
      <Route path="/admin/work-hours" element={<AdminRoute><WorkHoursPage /></AdminRoute>} />
      <Route path="/admin/tutorial" element={<AdminRoute><TutorialSettingsPage /></AdminRoute>} />
      <Route path="/admin/qr-manage" element={<AdminRoute><QRCodeManagePage /></AdminRoute>} />
      <Route path="/admin/settlement-overview" element={<AdminRoute><SettlementOverviewPage /></AdminRoute>} />
      <Route path="/admin/agent-settlement-overview" element={<AdminRoute><AgentSettlementOverviewPage /></AdminRoute>} />
      <Route path="/admin/phone-query" element={<AdminRoute><PhoneQueryPage /></AdminRoute>} />
      <Route path="/admin/images" element={<AdminRoute><ImageManagePage /></AdminRoute>} />
      <Route path="/admin/images/:userId" element={<AdminRoute><ImageManagePage /></AdminRoute>} />
      <Route path="/admin/ocr" element={<AdminRoute><AdminOcrPage /></AdminRoute>} />
      <Route path="/admin/push" element={<AdminRoute><PushMessagePage /></AdminRoute>} />
      <Route path="/admin/submission-rate" element={<AdminRoute><SubmissionRatePage /></AdminRoute>} />
      <Route path="/admin/daily-summary" element={<AdminRoute><DailySummaryPage /></AdminRoute>} />
      <Route path="/admin/rate-control" element={<AdminRoute><RateControlPage /></AdminRoute>} />
      <Route path="/admin/video-review" element={<AdminRoute><VideoReviewPage /></AdminRoute>} />
      <Route path="/admin/puzzle-packages" element={<AdminOnlyRoute><PuzzlePackagePage /></AdminOnlyRoute>} />
      <Route path="/admin/puzzle-proxy" element={<AdminRoute><PuzzleProxyPage /></AdminRoute>} />
      <Route path="/admin/puzzle-records" element={<AdminRoute><AdminPuzzleRecordsPage /></AdminRoute>} />
      <Route path="/admin/puzzle-images" element={<PuzzleImagesRedirect />} />
      <Route path="/admin/puzzle-settlement" element={<Navigate to="/admin/settlement" replace />} />
      <Route path="/video-review" element={<ProtectedRoute><VideoReviewUserPage /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </MaintenanceGate>
  );
}

function App() {
  // 微信内置浏览器：全屏拦截，引导用户从外部浏览器打开
  if (isWeChatBrowser()) return <WeChatBlocker />;

  return (
    <BrowserRouter>
      <AuthProvider>
        <ToastProvider>
          <ConfirmProvider>
            <SocketProvider>
              <AppRoutes />
            </SocketProvider>
          </ConfirmProvider>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
